import { useState } from 'react';

import styled from 'styled-components';
import { FaArrowUp, FaTrash } from 'react-icons/fa';

import { NEW_DAY_LABELS } from 'constants/dateAndTime';

import { TotemPrimaryButton } from 'components/TotemPrimaryButton';
import { TotemPrimaryLoadingButton } from 'components/TotemPrimaryLoadingButton';
import { Option, TotemSelect } from 'components/TotemSelect';

import {
    urbantzRoundPresetsDeletedHandler,
    urbantzRoundPresetsUpdatedOrCreatedHandler,
} from './cacheHandlers/urbantzRoundPresetsCacheHandler';

import {
    useUpdateUrbantzRoundPresetMutation,
    useDeleteUrbantzRoundPresetMutation,
    UrbantzRoundPresetFragmentFragment,
    TotadminPermission,
} from 'data/__generated__';
import { confirmPopup } from 'components/Popups/Confirm';

export function UrbantzRoundPresetForm({
    totemOptions,
    deliveryPeopleOptions,
    urbantzRoundPreset,
}: {
    totemOptions: Option<string>[];
    deliveryPeopleOptions: Option<string>[];
    urbantzRoundPreset: UrbantzRoundPresetFragmentFragment;
}) {
    const [totemIds, setTotemIds] = useState<string[]>(urbantzRoundPreset.totemIds);
    const [deliveryPersonUserId, setDeliveryPersonUserId] = useState<string | null>(
        urbantzRoundPreset.deliveryPersonUserId ?? null,
    );

    const [updateUrbantzRoundPreset, { loading: updateLoading }] = useUpdateUrbantzRoundPresetMutation();

    const [deleteUrbantzRoundPreset, { loading: deleteLoading }] = useDeleteUrbantzRoundPresetMutation();

    async function update() {
        const { data } = await updateUrbantzRoundPreset({
            variables: {
                urbantzRoundPresetUpdateInput: { _id: urbantzRoundPreset._id, deliveryPersonUserId, totemIds },
            },
        });

        if (!data?.updateUrbantzRoundPreset) {
            throw new Error('Une erreur est survenue lors de la mise à jour de la tournée');
        }

        const { updateUrbantzRoundPreset: updatedUrbantzRoundPreset } = data;

        urbantzRoundPresetsUpdatedOrCreatedHandler([updatedUrbantzRoundPreset]);
    }

    async function deleteRound() {
        const hasConfirmed = await confirmPopup({
            title: 'Confirmer',
            message: 'Êtes-vous sûr.e de vouloir supprimer la tournée ?',
        });
        if (!hasConfirmed) {
            return;
        }

        const { data } = await deleteUrbantzRoundPreset({
            variables: { urbantzRoundPresetId: urbantzRoundPreset._id },
        });

        if (!data?.deleteUrbantzRoundPreset) {
            throw new Error('Une erreur est survenue lors de la suppression de la tournée');
        }

        urbantzRoundPresetsDeletedHandler([urbantzRoundPreset._id]);
    }

    function moveTotemUp(index: number) {
        if (!index) {
            return;
        }

        const newTotemIds = [...totemIds];
        const temp = newTotemIds[index - 1];
        newTotemIds[index - 1] = newTotemIds[index];
        newTotemIds[index] = temp;
        setTotemIds(newTotemIds);
    }

    function deleteTotem(index: number) {
        const newTotemIds = [...totemIds];
        newTotemIds.splice(index, 1);
        setTotemIds(newTotemIds);
    }

    const hasChanged =
        urbantzRoundPreset.totemIds.toString() !== totemIds.toString() ||
        (urbantzRoundPreset.deliveryPersonUserId ?? null) !== deliveryPersonUserId;

    return (
        <Container>
            <DayContent>{NEW_DAY_LABELS[urbantzRoundPreset.dayIndex]}</DayContent>
            <SelectContainer>
                <TotemSelect
                    label="Appro"
                    placeholder="Sélectionner un livreur"
                    isClearable
                    disabled={updateLoading}
                    value={deliveryPersonUserId}
                    options={deliveryPeopleOptions}
                    onChange={setDeliveryPersonUserId}
                />
            </SelectContainer>
            {totemIds.map((totemId, index) => {
                const totemOption = totemOptions.find(({ value }) => value === totemId);
                return (
                    <TotemContainer key={`${totemId}-${index}`}>
                        <TotemLabel>
                            {index + 1}. {totemOption?.label ?? 'Erreur'}
                        </TotemLabel>
                        {index ? (
                            <SmallButton onClick={() => moveTotemUp(index)}>
                                <FaArrowUp size={15} />
                            </SmallButton>
                        ) : null}
                        <SmallButton onClick={() => deleteTotem(index)}>
                            <FaTrash size={15} />
                        </SmallButton>
                    </TotemContainer>
                );
            })}
            <SelectContainer>
                <TotemSelect
                    placeholder="Sélectionner un totem"
                    disabled={updateLoading}
                    value={null}
                    options={totemOptions}
                    onChange={(value) => {
                        if (value) {
                            setTotemIds([...totemIds, value]);
                        }
                    }}
                />
            </SelectContainer>
            <ButtonContainer>
                <TotemPrimaryLoadingButton
                    disabled={!hasChanged}
                    onClick={update}
                    loading={updateLoading || deleteLoading}
                    useIcon={false}
                    label="Mettre à jour"
                    requiredPermissions={[TotadminPermission.ToursManage]}
                />
                <TotemPrimaryLoadingButton
                    disabled={hasChanged}
                    onClick={deleteRound}
                    loading={updateLoading || deleteLoading}
                    useIcon={false}
                    label="Supprimer"
                    requiredPermissions={[TotadminPermission.ToursManage]}
                />
            </ButtonContainer>
        </Container>
    );
}

const Container = styled.div`
    margin-top: 20px;
    padding: 10px;
    background-color: ${({ theme }) => theme.cardBackgroundColor};
    box-shadow: ${({ theme }) => theme.boxShadow};
    border-radius: 10px;
`;

const DayContent = styled.div`
    font-weight: bold;
`;

const ButtonContainer = styled.div`
    margin-top: 10px;

    > :not(:first-child) {
        margin-left: 10px;
    }
`;

const SelectContainer = styled.div`
    margin-top: 10px;
`;

const TotemContainer = styled.div`
    margin-top: 10px;
    display: flex;
    flex-direction: row;
`;
const TotemLabel = styled.div`
    margin-top: 10px;
    flex-grow: 1;
`;

const SmallButton = styled(TotemPrimaryButton)`
    padding: 10px;
    margin-left: 5px;
`;
